import React, { useEffect, useRef } from "react";
import { InputAdornment, Grid } from "@mui/material";
import PropTypes from "prop-types";
import { format, isValid } from "date-fns";

import {
  InlineRadioGroup,
  SelectAutocomplete,
  AutoSaveField as Field,
  Input,
  DatePicker
} from "../../../../components/Form";

import { yesNoOptions } from "../../../../constants/options";
import { DATE_FORMAT, STEP_STATUS } from "../../../../constants";
import { StyledLabel, StyledLabelContainer } from "./styled";
import { COMPANY_STAKEHOLDER_ID } from "../../../PostEligibilityCompanyStakeholders/constants";
import { PRE_ELIGIBILITY_COMPANY_STAKEHOLDER_ID, STEP_1 } from "../../constants";
import { cloneDeep } from "../../../../utils/cloneDeep";
import { FormatDecimalNumberInput } from "../../../../utils/financeFormUtils";

import { Card } from "../../../../components/v2/Card/Card";
import { blockMinus, limitDigits } from "../../../../utils/validation";

/**
 * Component to render the "Other" input field when "Other" is selected in authority types
 *
 * @param {Object} props - Component props
 * @param {number} props.index - Index of the stakeholder
 * @param {Array} props.values - Selected authority type values
 * @param {Array} props.authorityTypes - Available authority types
 * @returns {JSX.Element} - OthersInput component
 */
const OthersInput = ({ index, values, authorityTypes }) => {
  const isOtherSelected = () => {
    try {
      if (authorityTypes && values && values.length > 0) {
        return authorityTypes
          .filter(item => values.indexOf(item.value) !== -1)
          .some(item => item.displayText === "Other");
      } else {
        return false;
      }
    } catch (error) {
      console.error("Error occurred while checking if 'Other' is selected:", error);
      return false;
    }
  };
  return (
    <>
      {isOtherSelected() && (
        <Grid item sm={6} xs={12}>
          <Field
            name="other"
            path={`prospect.signatoryInfo[${index}].authorityTypeValue[0].others`}
            label="Other"
            placeholder="Other"
            component={Input}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
              inputProps: { tabIndex: 0 },
              inputComponent: FormatDecimalNumberInput
            }}
          />
        </Grid>
      )}
    </>
  );
};

OthersInput.propTypes = {
  index: PropTypes.number.isRequired,
  values: PropTypes.array,
  authorityTypes: PropTypes.array
};

const MemoizedOthersInput = React.memo(OthersInput);

/**
 * SignatoryAndShareholding component for managing signatory and shareholding related fields in a single card
 *
 * @param {Object} props - Component properties
 * @returns {JSX.Element} - SignatoryAndShareholding component
 */
export const SignatoryAndShareholding = ({
  values,
  setFieldValue,
  setFieldTouched,
  handleBlur,
  totalPercentageWithoutCurrentStakeholder,
  isSoleProprietor,
  index,
  stakeholder,
  formRef,
  authorityTypes,
  freeFieldsInfo,
  completedSteps,
  updateProspect,
  loadMetaData,
  isSignatory,
  isComeFromROScreens,
  refreshDocuments
}) => {
  const { isSignatory: stakeholderIsSignatory, isShareholder, authorityTypeValue } = stakeholder;
  const stakeholderId = stakeholder.id;
  const rightsRef = useRef(isSignatory);
  const ownershipMandateDisabled = stakeholderIsSignatory && isShareholder;

  // Initialize authority type values based on stakeholder data
  useEffect(() => {
    const { authorityTypeValue } = stakeholder;
    if (
      formRef?.current &&
      (authorityTypeValue.length === 0 || authorityTypeValue[0].authorityType === "")
    ) {
      formRef.current.setFieldValue("authorityType", []);
    }
  }, [stakeholder, formRef]);

  // Handle changes to isSignatory and update completed steps
  useEffect(() => {
    const clonedCompletedSteps = cloneDeep(completedSteps);
    if (rightsRef.current !== isSignatory) {
      rightsRef.current = isSignatory;
      if (isSignatory) {
        const newCompletedSteps = clonedCompletedSteps.map(completedStep => {
          if (
            completedStep.flowId === `${COMPANY_STAKEHOLDER_ID}${stakeholderId}` ||
            (completedStep.flowId === `${PRE_ELIGIBILITY_COMPANY_STAKEHOLDER_ID}${stakeholderId}` &&
              completedStep.step !== STEP_1)
          ) {
            return { ...completedStep, status: STEP_STATUS.RESET };
          }

          return completedStep;
        });

        const completedStepsStr = JSON.stringify({
          completedSteps: newCompletedSteps
        });
        freeFieldsInfo.freeField5 = completedStepsStr;
        updateProspect({
          "prospect.freeFieldsInfo.freeField5": completedStepsStr
        });

        loadMetaData(freeFieldsInfo);

        // Refresh documents when isSignatory changes to true and user is from RO screens
        // This ensures signatory-specific documents are fetched
      }
    }

    if (isComeFromROScreens && refreshDocuments) {
      refreshDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignatory]);

  // Shareholding handlers
  const createShareholderHandler = () => event => {
    const value = JSON.parse(event.target.value);
    setFieldValue("isShareholder", value);

    const shareHoldingPercentage = value !== values.isShareholder && !value ? "0" : "";
    setFieldValue("shareHoldingPercentage", shareHoldingPercentage);
  };

  const changeDateProspectHandler = (_, value, path) =>
    isValid(value) && { [path]: format(value, DATE_FORMAT) };

  const shareholderHandler = createShareholderHandler();

  return (
    <Card title="Signatory and shareholding">
      {/* Signatory Section */}
      <Field
        name="isSignatory"
        path={`prospect.signatoryInfo[${index}].isSignatory`}
        component={InlineRadioGroup}
        options={yesNoOptions}
        label="Is this person a signatory?"
        onSelect={e => {
          if (values.isSignatory) {
            setFieldValue("authorityType", []);
          }
        }}
        changeProspect={(prospect, value) => {
          if (values.isSignatory) {
            return prospect;
          }
          return {
            ...prospect,
            [`prospect.signatoryInfo[${index}].signatoryPowerHeldSince`]: ""
          };
        }}
        onBlur={handleBlur}
        InputProps={{
          inputProps: { tabIndex: 0 }
        }}
      />

      {!values.isSignatory && (
        <Field
          name="involvedInBusiness"
          disabled={values.isSignatory}
          path={`prospect.signatoryInfo[${index}].isInvolvedInBusiness`}
          component={InlineRadioGroup}
          options={yesNoOptions}
          label="Involved in the business?"
          InputProps={{
            inputProps: { tabIndex: 0 }
          }}
        />
      )}

      <div>
        <div>
          <StyledLabelContainer>
            <StyledLabel>Document Conferring Signatory Authority</StyledLabel>
          </StyledLabelContainer>
          <Field
            multiple
            name="authorityType"
            path={`prospect.signatoryInfo[${index}].authorityTypeValue`}
            disabled={!values.isSignatory}
            isSearchable
            component={SelectAutocomplete}
            label={window.innerWidth > 600 ? "Document Conferring Signatory Authority" : ""}
            datalistId="authorityType"
            contextualHelpProps={{ isDisableHoverListener: false }}
            contextualHelpText="Select the authority / document through which the stakeholder is nominated as Signatory"
            tabIndex="0"
            onChange={selectedValue => {
              setFieldValue("authorityType", [
                ...selectedValue.map(value => ({
                  authorityType: value,
                  others: ""
                }))
              ]);
            }}
            extractValue={value => value.authorityType}
          />
        </div>
        <MemoizedOthersInput
          index={index}
          values={values.authorityType}
          authorityTypes={authorityTypes}
        />
      </div>

      {values.isSignatory && (
        <Field
          name="signatoryPowerHeldDate"
          disabled={!values.isSignatory}
          path={`prospect.signatoryInfo[${index}].signatoryPowerHeldSince`}
          label="Signatory power held since"
          placeholder="Signatory power held since"
          component={DatePicker}
          changeProspect={changeDateProspectHandler}
          InputProps={{
            inputProps: { tabIndex: 0 }
          }}
          maxDate={new Date()}
        />
      )}

      {/* Shareholding Section */}
      <Field
        disabled={isSoleProprietor}
        name="isShareholder"
        component={InlineRadioGroup}
        path={`prospect.signatoryInfo[${index}].isShareholder`}
        options={yesNoOptions}
        label="Is this person a shareholder?"
        onChange={shareholderHandler}
        contextualHelpProps={{ isDisableHoverListener: false, placement: "bottom-end" }}
        contextualHelpText="Select 'Yes' if this person holds any shares based on Memorandum of Association/ Articles of Association/ Partners agreement/ Service Agreement/ Share Certificate"
        InputProps={{
          inputProps: { tabIndex: 0 }
        }}
        onSelect={e => {
          if (e.target.value === "true") {
            const initTypeOfOwnerShip = stakeholderIsSignatory
              ? authorityTypeValue.map(authorityType => authorityType)
              : [];
            setFieldValue("typeOfOwnerShip", initTypeOfOwnerShip);
            setFieldTouched("typeOfOwnerShip", false);
            setFieldValue("shareHoldingPercentage", "");
          } else {
            setFieldValue("typeOfOwnerShip", []);
            setFieldTouched("typeOfOwnerShip", false);
            setFieldValue("shareHoldingPercentage", "0");
            setFieldTouched("shareHoldingPercentage", true);

            // Update prospect to persist the changes
            updateProspect({
              [`prospect.signatoryInfo[${index}].typeOfOwnership`]: [],
              [`prospect.signatoryInfo[${index}].shareHoldingPercentage`]: "0"
            });
          }
        }}
      />

      {/* Only show typeOfOwnerShip if shareholder */}
      {values.isShareholder && (
        <Field
          multiple
          isSearchable
          name="typeOfOwnerShip"
          path={`prospect.signatoryInfo[${index}].typeOfOwnership`}
          label={`Ownership Mandate${values.isShareholder ? "" : "(optional)"}`}
          datalistId="authorityType"
          component={SelectAutocomplete}
          InputProps={{
            inputProps: { maxLength: 30, tabIndex: 0 }
          }}
          onChange={selectedValue => {
            setFieldValue("typeOfOwnerShip", [
              ...selectedValue.map(value => ({
                authorityType: value,
                others: ""
              }))
            ]);
          }}
          extractValue={value => value.authorityType}
          disabled={ownershipMandateDisabled}
        />
      )}

      {/* Always show shareHoldingPercentage field, disabled and with value '0' if not shareholder */}
      <Field
        name="shareHoldingPercentage"
        path={`prospect.signatoryInfo[${index}].shareHoldingPercentage`}
        label="Ownership percentage"
        placeholder={values.isShareholder ? "33.33" : "Ownership percentage"}
        disabled={!values.isShareholder || isSoleProprietor}
        component={Input}
        type="number"
        InputProps={{
          endAdornment: <InputAdornment position="end">%</InputAdornment>,
          inputProps: {
            min: 0,
            max: 100,
            step: 0.01,
            tabIndex: 0,
            onKeyDown: blockMinus,
            onInput: limitDigits,
            readOnly: !values.isShareholder || isSoleProprietor
          }
        }}
        contextualHelpText="Mention the percentage of shares held based on Memorandum of Association/ Articles of Association/ Partners agreement/ Service Agreement/ Share Certificate"
        value={values.isShareholder ? values.shareHoldingPercentage : "0"}
      />

      <Field
        type="hidden"
        name="isSignatory"
        path={`prospect.signatoryInfo[${index}].isSignatory`}
      />
    </Card>
  );
};

SignatoryAndShareholding.propTypes = {
  /**
   * Index of the stakeholder
   */
  index: PropTypes.number.isRequired,
  /**
   * Available authority types
   */
  authorityTypes: PropTypes.array,
  /**
   * Stakeholder data
   */
  stakeholder: PropTypes.shape({
    /**
     * Unique identifier for stakeholder
     */
    id: PropTypes.string.isRequired,
    /**
     * Flag indicating if stakeholder is a signatory
     */
    isSignatory: PropTypes.bool,
    /**
     * Flag indicating if stakeholder is a shareholder
     */
    isShareholder: PropTypes.bool,
    /**
     * Array of authority types associated with the stakeholder
     */
    authorityTypeValue: PropTypes.arrayOf(
      PropTypes.shape({
        /**
         * Type of authority
         */
        authorityType: PropTypes.string,
        /**
         * Other authority type (if applicable)
         */
        others: PropTypes.string
      })
    )
  }).isRequired,
  /**
   * Form values
   */
  values: PropTypes.shape({
    /**
     * Flag indicating if stakeholder is a signatory
     */
    isSignatory: PropTypes.bool,
    /**
     * Flag indicating if stakeholder is a shareholder
     */
    isShareholder: PropTypes.bool,
    /**
     * Array of authority types
     */
    authorityType: PropTypes.array,
    /**
     * Shareholding percentage value
     */
    shareHoldingPercentage: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  /**
   * Total percentage without current stakeholder
   */
  totalPercentageWithoutCurrentStakeholder: PropTypes.number,
  /**
   * Flag indicating if the stakeholder is a sole proprietor
   */
  isSoleProprietor: PropTypes.bool,
  /**
   * Free fields info from state
   */
  freeFieldsInfo: PropTypes.object,
  /**
   * Completed steps from state
   */
  completedSteps: PropTypes.array,
  /**
   * Function to update prospect data
   */
  updateProspect: PropTypes.func.isRequired,
  /**
   * Function to load metadata
   */
  loadMetaData: PropTypes.func.isRequired,
  /**
   * Flag indicating if stakeholder is a signatory
   */
  isSignatory: PropTypes.bool,
  /**
   * Function to handle input blur events
   */
  handleBlur: PropTypes.func,
  /**
   * Function to set field value
   */
  setFieldValue: PropTypes.func.isRequired,
  /**
   * Function to set field touched state
   */
  setFieldTouched: PropTypes.func.isRequired,
  /**
   * Reference to the form from parent component
   */
  formRef: PropTypes.shape({
    current: PropTypes.object
  }),
  /**
   * Flag indicating if user is coming from RO screens
   */
  isComeFromROScreens: PropTypes.bool,
  /**
   * Function to refresh documents from API
   */
  refreshDocuments: PropTypes.func
};
